import { Button, Container } from '@mui/material';
import { useCallback, useMemo, useRef, useState } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Adsense from '../Ads/Adsense';
import { formatKRW, formatManwon } from '../../utils/formatKRW';
import { useRetirementStore } from '../../store/retirementStore';
import { logEvent } from '../../utils/ga';
import { ExpertCallout } from '../shared/ExpertCallout';
import RetirementChart from './Chart/RetirementChart';
import { RetirementForm } from './Form/RetirementForm';
import {
  calculateRetirement,
  RetirementOptions,
  RetirementResult,
} from './RetirementPage.hooks';
import styles from './RetirementPage.module.css';

function getExpertCallout(result: RetirementResult) {
  if (result.isPerpetual) {
    return {
      title: '재정적 자립 달성',
      body: '이자 수익만으로 생활비를 충당할 수 있습니다. 인플레이션 방어를 위해 자산의 20~30%는 주식형 ETF에 유지하는 전략을 권장합니다.',
      cta: { label: '투자가이드에서 자산배분 전략 보기', href: '/guide' },
    };
  }
  const age = result.depletionAge ?? 0;
  if (age < 75) {
    return {
      title: `${age}세 소진은 심각한 부족입니다 — 재설계가 필요합니다`,
      body: '75세 이전 소진은 노후 생활이 10년 이상 공백으로 남습니다. 은퇴 전 추가 저축, 월 생활비 재점검, 은퇴 나이 2~3년 연장을 동시에 검토하세요.',
      cta: { label: '복리 시뮬레이터에서 자산 키우기', href: '/compound' },
    };
  }
  return {
    title: `${age}세 소진 — 조정으로 안심 범위 진입 가능`,
    body: '90세 이상 유지가 이상적입니다. 월 생활비를 소폭 줄이거나 목표 자산을 늘리면 큰 차이가 생깁니다.',
    cta: { label: '목표 설계 계산기로 저축액 역산', href: '/retirement/goal' },
  };
}

function RetirementPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const compoundFinalAmount = useRetirementStore((s) => s.compoundFinalAmount);
  const [result, setResult] = useState<RetirementResult>();
  const resultRef = useRef<HTMLDivElement>(null);

  const initialFromQuery = useMemo(() => {
    const asset = searchParams.get('asset');
    if (asset) return { totalAsset: Number(asset) };
    if (compoundFinalAmount) return { totalAsset: compoundFinalAmount };
    return undefined;
  }, [searchParams, compoundFinalAmount]);

  const handleSubmit = useCallback((opts: RetirementOptions) => {
    logEvent('calculator', 'retirement_calculate', 'submit');
    setResult(calculateRetirement(opts));
    setTimeout(() => resultRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 0);
  }, []);

  const callout = useMemo(() => (result ? getExpertCallout(result) : null), [result]);

  return (
    <div className={styles.root}>
      <Helmet>
        <title>노후 자금 계산기 — 은퇴 후 자산 소진 나이 시뮬레이션 | 노후든든</title>
        <meta
          name="description"
          content="은퇴 자산으로 몇 살까지 생활할 수 있는지 계산하세요. 월 생활비와 수익률 입력만으로 자산 소진 시점과 영구 보존 가능 여부를 즉시 확인합니다."
        />
      </Helmet>

      <Container maxWidth="sm">
        <div className={styles.pageHeader}>
          <h1 className={styles.pageTitle}>노후 자금 계산기</h1>
          <p className={styles.pageDesc}>
            은퇴 후 자산이 몇 살까지 유지될지 시뮬레이션합니다. 부족하다는 결과가 나와도 괜찮습니다 — 지금 아는 게 시작입니다.
          </p>
        </div>
        <div className={styles.formCard}>
          {!initialFromQuery && (
            <p className={styles.sourceHint}>
              먼저 <a href="/compound">복리 시뮬레이터</a>에서 은퇴 자산을 계산하면 자동으로 입력됩니다.
            </p>
          )}
          <RetirementForm
            className={styles.form}
            initialValues={initialFromQuery}
            onSubmit={handleSubmit}
          />
        </div>
      </Container>

      {result && (
        <div ref={resultRef}>
          <Container maxWidth="md" sx={{ mt: 4 }}>
            <div className={styles.resultSection}>
              {/* Hero */}
              <div
                className={`${styles.resultHeroCard} ${
                  result.isPerpetual
                    ? styles.resultHeroCardPerpetual
                    : styles.resultHeroCardDepletion
                }`}
              >
                <p className={styles.resultHeroLabel}>시뮬레이션 결과</p>
                <p className={styles.resultHeroAge}>
                  {result.isPerpetual ? '영구 보존 가능' : `${result.depletionAge}세에 자산 소진`}
                </p>
                <p className={styles.resultHeroInterp}>
                  {result.isPerpetual
                    ? '현재 계획대로라면 자산이 영구적으로 유지됩니다. 운용 수익이 생활비를 커버합니다.'
                    : `유지 가능 기간: ${Math.floor(result.depletionMonth / 12)}년 ${result.depletionMonth % 12}개월`}
                </p>
              </div>

              {/* Metrics */}
              <div className={styles.metricsRow}>
                <div className={styles.metricCard}>
                  <p className={styles.metricLabel}>월 이자 수익</p>
                  <p className={styles.metricValue}>{formatManwon(result.monthlyInterestIncome)}</p>
                </div>
                <div className={styles.metricCard}>
                  <p className={styles.metricLabel}>유지 가능 기간</p>
                  <p className={styles.metricValue}>
                    {result.isPerpetual
                      ? '무제한'
                      : `${Math.floor(result.depletionMonth / 12)}년`}
                  </p>
                </div>
                {!result.isPerpetual && (
                  <div className={styles.metricCard}>
                    <p className={styles.metricLabel}>영구 보존 필요 자산</p>
                    <p className={styles.metricValue}>{formatManwon(result.requiredAssetForPerpetual)}</p>
                  </div>
                )}
                {result.isPerpetual && (
                  <div className={styles.metricCard}>
                    <p className={styles.metricLabel}>월 흑자 (수익-지출)</p>
                    <p className={styles.metricValue}>
                      +{formatKRW(result.monthlyInterestIncome - (result.histories[0]?.withdrawal ?? 0))}
                    </p>
                  </div>
                )}
              </div>

              {/* Chart */}
              <div className={styles.chartCard}>
                <p className={styles.chartTitle}>연령별 잔여 자산 추이</p>
                <div className={styles.chartWrapper}>
                  <RetirementChart
                    histories={result.histories}
                    depletionAge={result.depletionAge}
                  />
                </div>
              </div>

              {/* Expert callout */}
              {callout && (
                <ExpertCallout title={callout.title} body={callout.body} cta={callout.cta} />
              )}

              {/* CTA */}
              <div className={styles.ctaRow}>
                <Button
                  variant="contained"
                  size="large"
                  onClick={() => {
                    logEvent('calculator', 'retirement_result_cta_click', '/retirement/goal');
                    navigate('/retirement/goal');
                  }}
                  sx={{ background: '#000033', '&:hover': { background: '#001166' } }}
                >
                  목표 자산 역산하기 →
                </Button>
              </div>

              <Adsense adClient="ca-pub-9359050149154028" adSlot="3275566807" />

              <p className={styles.disclaimer}>
                이 계산기는 투자 권유가 아닌 참고용 시뮬레이션입니다. 실제 수익은 시장 상황에 따라 다르며,
                세금·수수료·물가 상승률은 반영되지 않았습니다. 중요한 재무 결정은 전문가와 상담하세요.
              </p>
            </div>
          </Container>
        </div>
      )}
    </div>
  );
}

export default RetirementPage;
